const CHECKS = [
  {
    id: "period",
    label: "Reporting period validation",
    badge: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    acc: "text-cyan-300",
    rules: [
      "Source reporting date is detected and compared against the previous-month master",
      "Current reporting period is propagated into the generated master from the source",
      "Unrelated system dates and current-date fields are ignored — only the reporting period field is used",
      "Mismatched or missing reporting dates cause the source to be rejected before processing",
    ],
  },
  {
    id: "titlecode",
    label: "Title Code resolution",
    badge: "bg-violet-500/20 text-violet-300 border-violet-500/40",
    acc: "text-violet-300",
    rules: [
      "Title Code is the primary key — every insertion must resolve to exactly one master row",
      "For IT source: row alignment locates the candidate row, then its Title Code is validated against expectations",
      "For departmental sources: Title Code is read directly and looked up in the master index",
      "Item Name and Item Name Details are validated alongside the Title Code for structured sources",
      "Failed lookups do not blindly erase existing values — they are flagged or skipped",
    ],
  },
  {
    id: "formula",
    label: "Formula cell protection",
    badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    acc: "text-emerald-300",
    rules: [
      "Before any insertion, the target cell is inspected for a formula",
      "Formula-bearing cells are never overwritten — the source value is skipped and a formula skip event is logged",
      "Bangladesh Bank formula-derived cells continue to derive their values from the updated inputs automatically",
      "Formula detection applies to both Numeric Value and Textual Value columns",
    ],
  },
  {
    id: "conflict",
    label: "Conflict and duplicate detection",
    badge: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    acc: "text-amber-300",
    rules: [
      "Multiple source candidates for the same master row trigger a conflict — not a silent overwrite",
      "N/A values that require business interpretation are never auto-resolved",
      "Conflicting records are routed to the review queue with full source context",
      "A source that is not authoritative for a zone is suppressed without creating a conflict",
    ],
  },
  {
    id: "type",
    label: "Numeric and textual type handling",
    badge: "bg-sky-500/20 text-sky-300 border-sky-500/40",
    acc: "text-sky-300",
    rules: [
      "Each master row specifies whether its value field is Numeric Value or Textual Value",
      "Source values are type-checked before insertion — a numeric source cannot write to a textual cell and vice versa",
      "Type mismatches are logged and the row is skipped or routed to review",
    ],
  },
];

export default function ValidationEngine() {
  return (
    <div className="space-y-4">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">VALIDATION ENGINE</p>
        <h4 className="mt-1 text-sm font-semibold text-white">Data integrity checks — five layers</h4>
        <p className="mt-1 text-xs text-slate-400">
          Every source value passes through five validation layers before touching the master workbook. Any failure routes to a review item or a skip event — nothing is silently overwritten or lost.
        </p>
      </div>

      <div className="space-y-2">
        {CHECKS.map((c, i) => (
          <div key={c.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
            <div className="mb-2 flex items-center gap-2">
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border font-mono text-[9px] font-bold ${c.badge}`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className={`text-xs font-semibold ${c.acc}`}>{c.label}</span>
            </div>
            <ul className="space-y-1">
              {c.rules.map((r) => (
                <li key={r} className="flex gap-1.5 text-[11px] text-slate-400">
                  <span className={`mt-0.5 shrink-0 ${c.acc}`}>›</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Protections summary */}
      <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-3">
        <p className="font-mono text-[10px] tracking-[0.2em] text-red-400">HARD PROTECTIONS — NEVER BYPASSED</p>
        <ul className="mt-2 space-y-1">
          {[
            "BB formula cells are never overwritten — formula skip is logged and processing continues",
            "Failed source lookups do not erase existing master values",
            "Multiple competing candidates for one row go to review — not silent last-write-wins",
            "N/A values requiring business interpretation remain review-controlled",
            "Non-authoritative sources cannot write to zones they don't own",
          ].map((p) => (
            <li key={p} className="flex gap-1.5 text-[11px] text-slate-300">
              <span className="mt-0.5 shrink-0 text-red-400">›</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
